// components/dashboard/MobileDrawer.jsx
import { useEffect } from "react"
import { Link } from "react-router-dom"
import {
  Terminal, Briefcase, Flame, Inbox, User,
  Sun, Moon, LogOut, Sparkles, X,
} from "lucide-react"

const FF    = "'Euclid Circular A', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
const BRAND = "#18181b"

// ══════════════════════════════════════════════════════════════
// MOBILE DRAWER
// Props:
//   open        — boolean
//   onClose     — () => void
//   activeTab   — string
//   onTabClick  — (tab: string) => void
//   isDark      — boolean
//   onToggleTheme — () => void
//   C           — theme object
//   user        — Firebase auth user
//   userData    — Firestore user doc
//   onSignOut   — async () => void
//   logoImg     — light logo
//   logoImgDark — dark logo
// ══════════════════════════════════════════════════════════════
export default function MobileDrawer({
  open,
  onClose,
  activeTab,
  onTabClick,
  isDark,
  onToggleTheme,
  C,
  user,
  userData,
  onSignOut,
  logoImg,
  logoImgDark,
}) {
  const displayName     = userData?.name || user?.displayName || ""
  const avatarInitial   = displayName ? displayName[0].toUpperCase() : "?"
  const applicationsLeft = userData?.applicationsLeft ?? 24

  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden"
    else document.body.style.overflow = ""
    return () => { document.body.style.overflow = "" }
  }, [open])

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose])

  const NAV_ITEMS = [
    { id: "console", label: "Console", icon: <Terminal size={17} />, type: "tab" },
    { id: "applications", label: "Applications", icon: <Briefcase size={17} />, type: "tab" },
    { id: "wild", label: "In the wild", icon: <Flame size={17} />, type: "tab" },
    { id: "inbox", label: "Inbox", icon: <Inbox size={17} />, type: "tab" },
    { id: "profile", label: "Profile", icon: <User size={17} />, type: "tab" },
  ]

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 300,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: "fixed", top: 0, left: 0, bottom: 0,
        width: 272,
        background: C.bgSidebar,
        borderRight: `1px solid ${C.sbBorder}`,
        zIndex: 301,
        display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
        overflowY: "auto",
      }}>

        {/* ── Header: logo + close ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 16px 14px",
          borderBottom: `1px solid ${C.sbDivider}`,
          flexShrink: 0,
        }}>
          <Link to="/" onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <img
              src={isDark ? logoImgDark : logoImg}
              alt="Resolut"
              style={{ width: 30, height: 30, borderRadius: 8, objectFit: "contain", flexShrink: 0 }}
            />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FF, letterSpacing: "-0.02em" }}>
              Resolut
            </span>
          </Link>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: C.bgChip, border: `1px solid ${C.borderColor}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: C.textDim, flexShrink: 0,
              transition: "background 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.bgMuted}
            onMouseLeave={e => e.currentTarget.style.background = C.bgChip}
          >
            <X size={13} strokeWidth={2.5} />
          </button>
        </div>

        {/* ── Nav items ── */}
        <nav style={{ padding: "10px 10px 8px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map(item => {
            const isActive = activeTab === item.id
            const inner = (
              <>
                <span style={{
                  color: isActive ? C.sbActiveText : C.sbText,
                  display: "flex", flexShrink: 0,
                }}>
                  {item.icon}
                </span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: isActive ? 500 : 400, fontFamily: FF }}>
                  {item.label}
                </span>
                {item.badge && (
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    background: BRAND, color: "#fff",
                    borderRadius: 4, padding: "1px 5px", letterSpacing: "0.06em",
                  }}>
                    {item.badge}
                  </span>
                )}
              </>
            )

            const sharedStyle = {
              display: "flex", alignItems: "center", gap: 10,
              width: "100%", padding: "9px 10px",
              borderRadius: 8, cursor: "pointer", textAlign: "left",
              textDecoration: "none",
              background: isActive ? C.sbActive : "none",
              color: isActive ? C.sbActiveText : C.sbText,
              border: "none",
              transition: "background 0.12s",
            }

            if (item.type === "link") {
              return (
                <Link key={item.id} to={item.to} onClick={onClose} style={sharedStyle}>
                  {inner}
                </Link>
              )
            }
            return (
              <button
                key={item.id}
                onClick={() => { onTabClick(item.id); onClose() }}
                style={sharedStyle}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = C.sbHov }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "none" }}
              >
                {inner}
              </button>
            )
          })}
        </nav>

        {/* ── Divider ── */}
        <div style={{ height: 1, background: C.sbDivider, margin: "0 10px" }} />

        {/* ── Bottom section ── */}
        <div style={{ padding: "10px 10px 20px", display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Theme toggle */}
          <button
            onClick={onToggleTheme}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              width: "100%", padding: "9px 10px",
              border: "none", background: "none", borderRadius: 8,
              cursor: "pointer", fontFamily: FF, fontSize: 13.5,
              fontWeight: 400, color: C.sbText,
              transition: "background 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.sbHov}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <span style={{ color: C.sbText, display: "flex", flexShrink: 0 }}>
              {isDark ? <Sun size={17} /> : <Moon size={17} />}
            </span>
            <span style={{ flex: 1 }}>{isDark ? "Light Mode" : "Dark Mode"}</span>
            {/* Mini toggle pill */}
            <div style={{
              width: 34, height: 19, borderRadius: 10,
              background: isDark ? BRAND : "#e5e7eb",
              position: "relative", transition: "background 0.2s", flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: 1.5,
                left: isDark ? 17 : 2,
                width: 16, height: 16, borderRadius: "50%",
                background: "#fff", transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }} />
            </div>
          </button>

          {/* Divider */}
          <div style={{ height: 1, background: C.sbDivider, margin: "6px 2px" }} />

          {/* Upgrade pill */}
          <div style={{
            margin: "2px 0 6px",
            padding: "10px 12px",
            background: C.sbActiveBg || C.sbActive,
            borderRadius: 10,
            border: `1px solid ${C.sbActiveBorder || (C.isDark ? "#2a4a38" : "#c6ddd3")}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: C.sbActiveText || (C.isDark ? "#18181b" : BRAND), fontFamily: FF, fontWeight: 600 }}>
                PLAN
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.sbActiveText || (C.isDark ? "#18181b" : BRAND), fontFamily: FF }}>
                {applicationsLeft} credits
              </span>
            </div>
            <button
              onClick={() => {/* navigate to upgrade */}}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                width: "100%", padding: "7px 0",
                background: C.isDark ? "#18181b" : BRAND,
                border: "none", borderRadius: 7,
                cursor: "pointer", fontSize: 11, fontWeight: 600,
                color: C.isDark ? "#0e1a14" : "#fff", fontFamily: FF,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <Sparkles size={11} /> Upgrade
            </button>
          </div>

          {/* Profile row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 10px", borderRadius: 10,
            background: C.bgMuted,
            border: `1px solid ${C.borderColor}`,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: BRAND, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#ffffff", fontFamily: FF }}>
                {avatarInitial}
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName || "—"}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: C.textTertiary, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user?.email || ""}
              </p>
            </div>
          </div>

          {/* Sign out */}
          <button
            onClick={() => { onClose(); onSignOut() }}
            style={{
              display: "flex", alignItems: "center", gap: 9,
              width: "100%", padding: "9px 10px",
              border: "none", background: "none", borderRadius: 8,
              cursor: "pointer", textAlign: "left",
              fontSize: 13, fontWeight: 500, fontFamily: FF,
              color: "#dc2626", transition: "background 0.1s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.isDark ? "#2a1a1a" : "#fef2f2"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <LogOut size={15} color="#dc2626" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  )
}
